/**
 * MCP Submissions — OTF and RTF (TRUS-149 / TRUS-366)
 *
 * The outbound half of the MIF work. The loader takes what a health plan sends;
 * this sends the answer back, under the same rule: nothing goes out that this
 * organization did not enter.
 *
 * Three steps, and each exists because of something that goes wrong without it:
 *
 *   Spec     the columns to report, derived from the inbound pack's own
 *            organization-owned set. A hand-typed second list drifts from the
 *            first, and the drift only shows up as a rejected file.
 *   Build    the file, per member, with the reason anyone is missing — read
 *            BEFORE it is sent rather than after a rejection notice.
 *   Sent     what was actually transmitted, kept as bytes. Member data moves, so
 *            a regenerated file is not evidence of what was submitted.
 *
 * The dry run is deliberately unskippable: "Build" never sends. Sending is a
 * separate, explicit action on a file you have already looked at.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  ArrowUpFromLine,
  Check,
  CheckCircle2,
  ChevronRight,
  ClipboardList,
  Copy,
  FileText,
  Layers,
  Loader2,
  Send,
  ShieldCheck,
  UserMinus,
  Users,
} from 'lucide-react';

import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';

import { listPacks } from '@/services/api/mifImport';
import type { MifPack } from '@/services/api/mifImport';

import {
  BATCH_STATUS_LABELS,
  SOURCE_KIND_LABELS,
  SUBMISSION_TYPE_LABELS,
  buildSubmission,
  deriveSubmissionLayout,
  getSubmissionFile,
  getSubmissionLayout,
  listSubmissionBatches,
  listSubmissionLayouts,
  listSubmissionRows,
  recordSubmissionDecision,
  setSubmissionLayoutStatus,
  submitSubmission,
} from '@/services/api/mifSubmissions';
import type {
  BuildResult,
  SubmissionBatch,
  SubmissionColumn,
  SubmissionLayout,
  SubmissionRow,
  SubmissionType,
} from '@/services/api/mifSubmissions';

type Step = 'spec' | 'build' | 'sent';

const STEPS: { key: Step; label: string }[] = [
  { key: 'spec', label: 'What we report' },
  { key: 'build', label: 'Build and review' },
  { key: 'sent', label: 'Sent files' },
];

const errorText = (error: any, fallback: string): string => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail?.message) return detail.message;
  return error?.message || fallback;
};

/**
 * Status as colour. A submission sitting in "needs attention" looks different at a
 * glance from one the plan accepted — the whole point of the history table is
 * spotting the one that went wrong without reading every row.
 */
const STATUS_TONE: Record<SubmissionBatch['status'], 'success' | 'danger' | 'warning' | 'secondary'> =
  {
    draft: 'warning',
    validated: 'secondary',
    submitted: 'secondary',
    accepted: 'success',
    rejected: 'danger',
    failed: 'danger',
    superseded: 'secondary',
  };

/** The period string SFHP uses: MM/DD/YYYY.MM/DD/YYYY for the month just closed. */
const defaultPeriod = (): string => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const end = new Date(now.getFullYear(), now.getMonth(), 0);
  const fmt = (d: Date) =>
    `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${d.getFullYear()}`;
  return `${fmt(start)}.${fmt(end)}`;
};

// ===========================================================================

export const McpSubmissions: React.FC = () => {
  const [step, setStep] = useState<Step>('spec');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [packs, setPacks] = useState<MifPack[]>([]);
  const [layouts, setLayouts] = useState<SubmissionLayout[]>([]);
  const [batches, setBatches] = useState<SubmissionBatch[]>([]);

  const [activeLayoutId, setActiveLayoutId] = useState<number | null>(null);
  const [build, setBuild] = useState<BuildResult | null>(null);

  const approvedPack = useMemo(() => packs.find((p) => p.status === 'approved'), [packs]);

  const refresh = useCallback(async () => {
    try {
      const [packData, layoutData, batchData] = await Promise.all([
        listPacks(),
        listSubmissionLayouts(),
        listSubmissionBatches(),
      ]);
      setPacks(packData.packs || []);
      setLayouts(layoutData.layouts || []);
      setBatches(batchData.batches || []);
    } catch (err) {
      setError(errorText(err, 'Could not load submissions.'));
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleDerive = async (packId: number, submissionType: SubmissionType, version: string) => {
    setBusy('derive');
    setError(null);
    try {
      const result = await deriveSubmissionLayout({
        pack_id: packId,
        submission_type: submissionType,
        layout_version: version,
      });
      setActiveLayoutId(result.layout_id);
      setNotice(result.message);
      await refresh();
    } catch (err) {
      setError(errorText(err, 'Could not build the spec from that pack.'));
    } finally {
      setBusy(null);
    }
  };

  const handleBuild = async (layoutId: number, period: string) => {
    setBusy('build');
    setError(null);
    setNotice(null);
    try {
      const result = await buildSubmission({ layout_id: layoutId, reporting_period: period });
      setBuild(result);
      setActiveLayoutId(layoutId);
      setStep('build');
      await refresh();
    } catch (err) {
      setError(errorText(err, 'Could not build the file.'));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="flex items-center gap-2 text-xl font-semibold">
            <ArrowUpFromLine className="h-5 w-5 text-primary" />
            Submissions to health plans
          </h2>
          <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
            Outreach (OTF) and return (RTF) files. Building never sends — you see the file, and who is
            in it, before anything leaves.
          </p>
        </div>
        <StepBar current={step} onGo={setStep} />
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>That didn't work</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {notice && (
        <Alert>
          <CheckCircle2 className="h-4 w-4" />
          <AlertDescription>{notice}</AlertDescription>
        </Alert>
      )}

      {step === 'spec' && (
        <SpecStep
          packs={packs}
          approvedPack={approvedPack}
          layouts={layouts}
          busy={busy}
          onDerive={handleDerive}
          onBuild={handleBuild}
          onChanged={refresh}
          onError={setError}
        />
      )}

      {step === 'build' && (
        <BuildStep
          build={build}
          layoutId={activeLayoutId}
          layouts={layouts}
          onSent={async () => {
            setBuild(null);
            setStep('sent');
            await refresh();
          }}
          onBack={() => setStep('spec')}
          onError={setError}
        />
      )}

      {step === 'sent' && <SentStep batches={batches} onChanged={refresh} onError={setError} />}
    </div>
  );
};

// ===========================================================================

const StepBar: React.FC<{ current: Step; onGo: (s: Step) => void }> = ({ current, onGo }) => {
  const index = STEPS.findIndex((s) => s.key === current);
  return (
    <div className="flex items-center gap-1 overflow-x-auto pb-1">
      {STEPS.map((s, i) => (
        <React.Fragment key={s.key}>
          <button
            type="button"
            onClick={() => onGo(s.key)}
            className="flex items-center gap-2 whitespace-nowrap"
          >
            <span
              className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold ${
                i < index
                  ? 'bg-primary text-primary-foreground'
                  : i === index
                    ? 'border-2 border-primary text-primary'
                    : 'border border-muted-foreground/30 text-muted-foreground'
              }`}
            >
              {i < index ? <Check className="h-3.5 w-3.5" /> : i + 1}
            </span>
            <span
              className={`text-sm ${i === index ? 'font-medium text-foreground' : 'text-muted-foreground'}`}
            >
              {s.label}
            </span>
          </button>
          {i < STEPS.length - 1 && (
            <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground/40" />
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

// ===========================================================================
// Step 1 — the spec
// ===========================================================================

const SpecStep: React.FC<{
  packs: MifPack[];
  approvedPack?: MifPack;
  layouts: SubmissionLayout[];
  busy: string | null;
  onDerive: (packId: number, type: SubmissionType, version: string) => void;
  onBuild: (layoutId: number, period: string) => void;
  onChanged: () => void;
  onError: (message: string) => void;
}> = ({ packs, approvedPack, layouts, busy, onDerive, onBuild, onChanged, onError }) => {
  const [submissionType, setSubmissionType] = useState<SubmissionType>('rtf');
  const [version, setVersion] = useState('v1');
  const [openLayoutId, setOpenLayoutId] = useState<number | null>(null);

  const published = layouts.filter((l) => l.status === 'published');
  const drafts = layouts.filter((l) => l.status === 'draft');

  return (
    <div className="space-y-4">
      {!approvedPack && (
        <Alert>
          <ShieldCheck className="h-4 w-4" />
          <AlertTitle>Set up the inbound rules first</AlertTitle>
          <AlertDescription>
            What you report back is taken from the columns your inbound rule pack says your
            organization owns — the ones it refuses to let the plan overwrite. Approve a pack on the
            Health Plan File tab and this spec builds itself from it.
          </AlertDescription>
        </Alert>
      )}

      {approvedPack && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Layers className="h-4 w-4" />
              Build the spec from {approvedPack.plan_name}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {approvedPack.protected_count} of this pack's {approvedPack.rule_count} columns are
              marked as owned by your organization. Those are exactly the ones the plan echoes back
              from your last submission — so they are what goes in this file, and taking them from the
              pack means the two lists cannot drift apart.
            </p>

            <div className="flex flex-wrap items-end gap-3">
              <label className="text-sm">
                <span className="mb-1 block text-muted-foreground">File type</span>
                <select
                  className="h-9 rounded-md border bg-background px-2 text-sm"
                  value={submissionType}
                  onChange={(e) => setSubmissionType(e.target.value as SubmissionType)}
                >
                  <option value="rtf">{SUBMISSION_TYPE_LABELS.rtf}</option>
                  <option value="otf">{SUBMISSION_TYPE_LABELS.otf}</option>
                  <option value="other">{SUBMISSION_TYPE_LABELS.other}</option>
                </select>
              </label>
              <label className="text-sm">
                <span className="mb-1 block text-muted-foreground">Version</span>
                <Input
                  className="h-9 w-28"
                  value={version}
                  onChange={(e) => setVersion(e.target.value)}
                  placeholder="v1"
                />
              </label>
              <Button
                onClick={() => onDerive(approvedPack.id, submissionType, version.trim() || 'v1')}
                disabled={busy === 'derive' || !version.trim()}
              >
                {busy === 'derive' ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Copy className="mr-2 h-4 w-4" />
                )}
                Build spec
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Lands as a draft. Which of those columns a given plan wants in an OTF versus an RTF is
              the plan's rule, not something the pack knows — so review it before publishing.
            </p>
          </CardContent>
        </Card>
      )}

      {drafts.length > 0 && (
        <LayoutTable
          title="Drafts"
          subtitle="Not yet usable. Review the columns, then publish."
          layouts={drafts}
          openLayoutId={openLayoutId}
          onToggle={setOpenLayoutId}
          onChanged={onChanged}
          onError={onError}
        />
      )}

      {published.length > 0 && (
        <LayoutTable
          title="Published"
          subtitle="Ready to build files against."
          layouts={published}
          openLayoutId={openLayoutId}
          onToggle={setOpenLayoutId}
          onChanged={onChanged}
          onError={onError}
          onBuild={onBuild}
          busy={busy}
        />
      )}

      {layouts.length === 0 && approvedPack && (
        <p className="text-sm text-muted-foreground">
          No submission spec yet. Build one from the pack above.
        </p>
      )}
    </div>
  );
};

const LayoutTable: React.FC<{
  title: string;
  subtitle: string;
  layouts: SubmissionLayout[];
  openLayoutId: number | null;
  onToggle: (id: number | null) => void;
  onChanged: () => void;
  onError: (message: string) => void;
  onBuild?: (layoutId: number, period: string) => void;
  busy?: string | null;
}> = ({ title, subtitle, layouts, openLayoutId, onToggle, onChanged, onError, onBuild, busy }) => {
  const [period, setPeriod] = useState(defaultPeriod());
  const [working, setWorking] = useState<number | null>(null);

  const publish = async (layout: SubmissionLayout, status: 'published' | 'retired') => {
    setWorking(layout.id);
    try {
      await setSubmissionLayoutStatus(layout.id, status);
      onChanged();
    } catch (err) {
      onError(errorText(err, 'Could not change that spec.'));
    } finally {
      setWorking(null);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">{title}</CardTitle>
        <p className="text-sm text-muted-foreground">{subtitle}</p>
      </CardHeader>
      <CardContent className="space-y-3">
        {layouts.map((layout) => (
          <div key={layout.id} className="rounded-lg border">
            <div className="flex flex-wrap items-center justify-between gap-3 p-3">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">{layout.plan_name}</span>
                  <Badge variant="outline">{SUBMISSION_TYPE_LABELS[layout.submission_type]}</Badge>
                  <Badge variant="secondary">{layout.layout_version}</Badge>
                  {layout.origin === 'derived' && (
                    <Badge variant="outline" className="gap-1">
                      <Copy className="h-3 w-3" />
                      From the inbound pack
                    </Badge>
                  )}
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  {layout.column_count} columns
                  {layout.sent_count > 0 && ` · ${layout.sent_count} file(s) sent`}
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <Button variant="ghost" size="sm" onClick={() => onToggle(openLayoutId === layout.id ? null : layout.id)}>
                  <ClipboardList className="mr-2 h-4 w-4" />
                  {openLayoutId === layout.id ? 'Hide columns' : 'Columns'}
                </Button>
                {layout.status === 'draft' && (
                  <Button size="sm" onClick={() => publish(layout, 'published')} disabled={working === layout.id}>
                    {working === layout.id ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                    Publish
                  </Button>
                )}
                {layout.status === 'published' && onBuild && (
                  <>
                    <Input
                      className="h-9 w-56"
                      value={period}
                      onChange={(e) => setPeriod(e.target.value)}
                      placeholder="Reporting period"
                    />
                    <Button
                      size="sm"
                      onClick={() => onBuild(layout.id, period.trim())}
                      disabled={busy === 'build' || !period.trim()}
                    >
                      {busy === 'build' ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <FileText className="mr-2 h-4 w-4" />
                      )}
                      Build file
                    </Button>
                  </>
                )}
              </div>
            </div>

            {openLayoutId === layout.id && <ColumnDetail layoutId={layout.id} onError={onError} />}
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

const ColumnDetail: React.FC<{ layoutId: number; onError: (m: string) => void }> = ({
  layoutId,
  onError,
}) => {
  const [columns, setColumns] = useState<SubmissionColumn[] | null>(null);

  useEffect(() => {
    getSubmissionLayout(layoutId)
      .then((data) => setColumns(data.columns))
      .catch((err) => onError(errorText(err, 'Could not load the columns.')));
  }, [layoutId, onError]);

  if (!columns) {
    return (
      <div className="flex items-center gap-2 border-t p-3 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading columns…
      </div>
    );
  }

  return (
    <div className="overflow-x-auto border-t">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-12">#</TableHead>
            <TableHead>Column the plan expects</TableHead>
            <TableHead>Where the value comes from</TableHead>
            <TableHead>Sent as</TableHead>
            <TableHead>Required</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {columns.map((column) => (
            <TableRow key={column.id}>
              <TableCell className="text-muted-foreground">{column.ordinal}</TableCell>
              <TableCell className="font-mono text-xs">{column.column_name}</TableCell>
              <TableCell className="text-sm">
                <span className="text-muted-foreground">{SOURCE_KIND_LABELS[column.source_kind]}</span>
                {column.source_field && (
                  <span className="ml-1 font-mono text-xs">{column.source_field}</span>
                )}
                {column.source_kind === 'field' && !column.source_field && (
                  <span className="ml-1 text-amber-600">no field mapped — will send blank</span>
                )}
              </TableCell>
              <TableCell>
                <Badge variant="outline" className="font-mono text-xs">
                  {column.format}
                </Badge>
              </TableCell>
              <TableCell>
                {column.required ? (
                  <Badge variant="secondary">Required</Badge>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

// ===========================================================================
// Step 2 — build and review
// ===========================================================================

const BuildStep: React.FC<{
  build: BuildResult | null;
  layoutId: number | null;
  layouts: SubmissionLayout[];
  onSent: () => void;
  onBack: () => void;
  onError: (m: string) => void;
}> = ({ build, layoutId, layouts, onSent, onBack, onError }) => {
  const [rows, setRows] = useState<SubmissionRow[]>([]);
  const [view, setView] = useState<'included' | 'excluded' | 'file'>('included');
  const [fileText, setFileText] = useState<string | null>(null);
  const [reference, setReference] = useState('');
  const [sending, setSending] = useState(false);

  const layout = layouts.find((l) => l.id === layoutId);

  useEffect(() => {
    if (!build) return;
    listSubmissionRows(build.batch_id)
      .then((data) => setRows(data.rows))
      .catch((err) => onError(errorText(err, 'Could not load the rows.')));
  }, [build, onError]);

  if (!build) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-sm text-muted-foreground">
          Nothing built yet.
          <div className="mt-3">
            <Button variant="outline" onClick={onBack}>
              Choose a spec
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  const included = rows.filter((r) => r.included);
  const excluded = rows.filter((r) => !r.included);
  const ready = build.status === 'validated';

  const showFile = async () => {
    setView('file');
    if (fileText === null) {
      try {
        setFileText(await getSubmissionFile(build.batch_id));
      } catch (err) {
        onError(errorText(err, 'Could not load the file.'));
      }
    }
  };

  const send = async () => {
    setSending(true);
    try {
      await submitSubmission(build.batch_id, reference.trim() || undefined);
      onSent();
    } catch (err) {
      onError(errorText(err, 'Could not record the submission.'));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <FileText className="h-4 w-4" />
            {build.file_name}
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            {layout ? `${layout.plan_name} · ${SUBMISSION_TYPE_LABELS[layout.submission_type]}` : ''}{' '}
            — built, not sent.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat label="Members considered" value={build.members_considered} icon={<Users className="h-4 w-4" />} />
            <Stat label="In the file" value={build.rows_included} tone="good" icon={<Check className="h-4 w-4" />} />
            <Stat
              label="Held back"
              value={build.rows_excluded}
              tone={build.rows_excluded ? 'warn' : undefined}
              icon={<UserMinus className="h-4 w-4" />}
            />
            <Stat
              label="Must fix first"
              value={build.errors}
              tone={build.errors ? 'bad' : 'good'}
              icon={<AlertTriangle className="h-4 w-4" />}
            />
          </div>

          <Alert variant={ready ? 'default' : 'destructive'}>
            {ready ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
            <AlertDescription>{build.message}</AlertDescription>
          </Alert>

          <Tabs value={view} onValueChange={(v) => (v === 'file' ? showFile() : setView(v as any))}>
            <TabsList>
              <TabsTrigger value="included">In the file ({included.length})</TabsTrigger>
              <TabsTrigger value="excluded">Held back ({excluded.length})</TabsTrigger>
              <TabsTrigger value="file">The file itself</TabsTrigger>
            </TabsList>

            <TabsContent value="included" className="mt-3">
              <RowTable rows={included} />
            </TabsContent>

            <TabsContent value="excluded" className="mt-3">
              {excluded.length === 0 ? (
                <p className="py-6 text-center text-sm text-muted-foreground">
                  Everyone qualifies. Nobody is being left out.
                </p>
              ) : (
                <>
                  <p className="mb-2 text-sm text-muted-foreground">
                    These members are not in the file. Each says why — a plan rejects a whole file
                    over one row it cannot match, so they are held back rather than sent malformed.
                  </p>
                  <RowTable rows={excluded} showReason />
                </>
              )}
            </TabsContent>

            <TabsContent value="file" className="mt-3">
              <pre className="max-h-96 overflow-auto rounded-lg border bg-muted/40 p-3 text-xs">
                {fileText ?? 'Loading…'}
              </pre>
              <p className="mt-2 text-xs text-muted-foreground">
                Checksum {build.file_sha256.slice(0, 16)}… — these exact bytes are what gets recorded
                as sent.
              </p>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Send className="h-4 w-4" />
            Record this as sent
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Marking it sent stamps the transmission date and period onto the {build.rows_included}{' '}
            member(s) in the file. That is what makes next month's file from the plan match what you
            reported.
          </p>
          <div className="flex flex-wrap items-end gap-3">
            <label className="text-sm">
              <span className="mb-1 block text-muted-foreground">
                The plan's reference, if it gave one
              </span>
              <Input
                className="h-9 w-64"
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                placeholder="Optional"
              />
            </label>
            <Button onClick={send} disabled={!ready || sending}>
              {sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
              Mark as sent
            </Button>
            <Button variant="outline" onClick={onBack}>
              Back to specs
            </Button>
          </div>
          {!ready && (
            <p className="text-xs text-destructive">
              {build.errors} problem(s) on rows we intend to send. Fix them and build again.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const RowTable: React.FC<{ rows: SubmissionRow[]; showReason?: boolean }> = ({ rows, showReason }) => {
  if (rows.length === 0) {
    return <p className="py-6 text-center text-sm text-muted-foreground">Nothing here.</p>;
  }
  return (
    <div className="max-h-96 overflow-auto rounded-lg border">
      <Table>
        <TableHeader>
          <TableRow>
            {!showReason && <TableHead className="w-16">Row</TableHead>}
            <TableHead>Member</TableHead>
            {showReason ? <TableHead>Why they are not in the file</TableHead> : <TableHead>Warnings</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={`${row.member_id}-${row.row_number}`}>
              {!showReason && (
                <TableCell className="text-muted-foreground">{row.row_number ?? '—'}</TableCell>
              )}
              <TableCell>{row.member_name || `Member ${row.member_id}`}</TableCell>
              <TableCell className="text-sm">
                {showReason ? (
                  <span className="text-amber-700 dark:text-amber-500">{row.exclusion_reason}</span>
                ) : (
                  (row.issues || [])
                    .filter((i) => i.severity === 'warning')
                    .map((i) => `${i.column}: ${i.message}`)
                    .join('; ') || <span className="text-muted-foreground">—</span>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

const Stat: React.FC<{
  label: string;
  value: React.ReactNode;
  tone?: 'good' | 'warn' | 'bad';
  icon?: React.ReactNode;
}> = ({ label, value, tone, icon }) => (
  <div className="rounded-lg border p-3">
    <div className="flex items-center gap-2 text-xs text-muted-foreground">
      {icon}
      {label}
    </div>
    <div
      className={`mt-1 text-2xl font-semibold ${
        tone === 'good'
          ? 'text-emerald-600'
          : tone === 'warn'
            ? 'text-amber-600'
            : tone === 'bad'
              ? 'text-destructive'
              : ''
      }`}
    >
      {value}
    </div>
  </div>
);

// ===========================================================================
// Step 3 — what was actually sent
// ===========================================================================

const SentStep: React.FC<{
  batches: SubmissionBatch[];
  onChanged: () => void;
  onError: (m: string) => void;
}> = ({ batches, onChanged, onError }) => {
  const [working, setWorking] = useState<number | null>(null);

  const decide = async (batchId: number, outcome: 'accepted' | 'rejected') => {
    setWorking(batchId);
    try {
      await recordSubmissionDecision(batchId, outcome);
      onChanged();
    } catch (err) {
      onError(errorText(err, 'Could not record the plan’s response.'));
    } finally {
      setWorking(null);
    }
  };

  const download = async (batch: SubmissionBatch) => {
    try {
      const text = await getSubmissionFile(batch.id);
      const blob = new Blob([text], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = batch.file_name || `submission-${batch.id}.txt`;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      onError(errorText(err, 'Could not download the file.'));
    }
  };

  if (batches.length === 0) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-sm text-muted-foreground">
          Nothing built or sent yet.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Submission history</CardTitle>
        <p className="text-sm text-muted-foreground">
          Each row keeps the bytes as they went out, not a recipe to rebuild them — member data moves,
          so a regenerated file would not answer "what did we send you".
        </p>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Period</TableHead>
              <TableHead>Plan / type</TableHead>
              <TableHead>Rows</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Sent</TableHead>
              <TableHead className="text-right">File</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {batches.map((batch) => (
              <TableRow key={batch.id}>
                <TableCell className="font-mono text-xs">{batch.reporting_period || '—'}</TableCell>
                <TableCell className="text-sm">
                  {batch.plan_name || batch.plan_key || '—'}
                  <span className="ml-1 text-muted-foreground">
                    {batch.submission_type.toUpperCase()}
                  </span>
                </TableCell>
                <TableCell className="text-sm">
                  {batch.rows_included}
                  {batch.rows_excluded > 0 && (
                    <span className="ml-1 text-muted-foreground">
                      ({batch.rows_excluded} held back)
                    </span>
                  )}
                </TableCell>
                <TableCell>
                  <Badge variant={STATUS_TONE[batch.status]}>
                    {BATCH_STATUS_LABELS[batch.status]}
                  </Badge>
                  {batch.rejection_note && (
                    <p className="mt-1 text-xs text-muted-foreground">{batch.rejection_note}</p>
                  )}
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {batch.submitted_at
                    ? `${new Date(batch.submitted_at).toLocaleDateString()}${
                        batch.submitted_by_name ? ` · ${batch.submitted_by_name}` : ''
                      }`
                    : '—'}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    {batch.status === 'submitted' && (
                      <>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => decide(batch.id, 'accepted')}
                          disabled={working === batch.id}
                        >
                          Accepted
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => decide(batch.id, 'rejected')}
                          disabled={working === batch.id}
                        >
                          Rejected
                        </Button>
                      </>
                    )}
                    <Button variant="outline" size="sm" onClick={() => download(batch)}>
                      Download
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

export default McpSubmissions;
